
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoginResponse } from '@api/user';

@Injectable()
export class ApiService {

  private getUrl(api: string): string {
    return environment.api[api];
  }

  public getBasicAuth(): string {
    return environment.basicAuth;
  }

  constructor(private httpClient: HttpClient) { }

  private getTimezoneHeader(): HttpHeaders {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    let login_response: LoginResponse = null;
    try {
      login_response = JSON.parse(sessionStorage.getItem('login_response'));
    } catch (e) {
      login_response = null;
    }
    return new HttpHeaders({
      'X-Timezone': timeZone || '',
      'X-Account-Id': login_response?.userDetails?.id_account != null ? String(login_response.userDetails.id_account) : '',
      'X-User-Email': login_response?.userDetails?.username != null ? String(login_response.userDetails.username) : '',
      'X-Professional-Id': login_response?.userDetails?.id_person != null ? String(login_response.userDetails.id_person) : '',
      'Content-Type': 'application/json',
    });
  }

  public get<T>(api: string, url: string, options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  }): Observable<T> {
    options = options || {};
    options.headers = options.headers ? options.headers : this.getTimezoneHeader();
    return this.httpClient.get<T>(`${this.getUrl(api)}${url}`, options);
  }

  /**
 * GET sin headers de usuario, solo X-Timezone. Para endpoints públicos.
 */
  public getPublic<T>(api: string, url: string, options?: {
    headers?: HttpHeaders | { [header: string]: string | string[] };
    observe?: 'body';
    params?: HttpParams | { [param: string]: string | string[] };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  }): Observable<T> {
    options = options || {};
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = new HttpHeaders({
      'X-Timezone': timeZone,
      'Content-Type': 'application/json',
    });
    options.headers = options.headers ? options.headers : headers;
    return this.httpClient.get<T>(`${this.getUrl(api)}${url}`, options);
  }

  public put<T>(api: string, url: string, model: any): Observable<T> {
    const headers = this.getTimezoneHeader();
    return this.httpClient.put<T>(`${this.getUrl(api)}${url}`, model, { headers });
  }

  public patch<T>(api: string, url: string, model: any): Observable<T> {
    const headers = this.getTimezoneHeader();
    return this.httpClient.patch<T>(`${this.getUrl(api)}${url}`, model, { headers });
  }

  postPublic<T>(api: string, url: string, model: any, options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  }): Observable<T> {
    options = options || {};
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = new HttpHeaders({
      'X-Timezone': timeZone,
      'Content-Type': 'application/json',
    });
    options.headers = options.headers ? options.headers : headers;
    return this.httpClient.post<T>(`${this.getUrl(api)}${url}`, model, options).pipe(share());
  }

  public post<T>(api: string, url: string, model: any, options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  }): Observable<T> {
    options = options || {};
    // Solo agrega Content-Type si NO es FormData
    if (!this.isFormData(model)) {
      options.headers = options.headers ? options.headers : this.getTimezoneHeader();
    } else {
      // Si es FormData, elimina Content-Type para que el navegador lo agregue automáticamente
      if (options.headers) {
        if (options.headers instanceof HttpHeaders) {
          options.headers = options.headers.delete('Content-Type');
        } else if (options.headers['Content-Type']) {
          delete options.headers['Content-Type'];
        }
      }
    }
    return this.httpClient.post<T>(`${this.getUrl(api)}${url}`, model, options).pipe(share());
  }

  private isFormData(model: any): boolean {
    return !!model
      && typeof model.append === 'function'
      && typeof model.delete === 'function'
      && typeof model.get === 'function';
  }

  public delete(api: string, url: string): Observable<any> {
    const headers = this.getTimezoneHeader();
    return this.httpClient.delete(`${this.getUrl(api)}${url}`, { headers });
  }

  public deleteWhithHeaders(api: string, url: string, headers: HttpHeaders): Observable<any> {
    return this.httpClient.delete(`${this.getUrl(api)}${url}`, { headers });
  }
}
